// 페이지 & API를 "리소스(resource) + 액션(action)"으로 식별하기 위한 공통 정의.
// - 리소스: 무엇에 대해 (page:..., api:...)
// - 액션: 무엇을 허용할지 (view/read/create/update/delete/...)
// 정책(policies)에서는 "특정 리소스에 대해 최소 역할(minRole)"을 선언하고,
// 가드/인터셉터에서는 현재 사용자의 role로 isAllowed를 판정한다.

export const ACTIONS = {
  // 화면/라우팅
  VIEW: 'view', // 페이지 접근
  NAVIGATE: 'navigate', // 메뉴/링크 노출, 이동

  // 데이터
  READ: 'read',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',

  // 업무 특화
  APPROVE: 'approve',
  REJECT: 'reject',
  EXPORT: 'export',
  IMPORT: 'import',
  PRINT: 'print',
  SYNC: 'sync',
  MANAGE: 'manage', // 관리(설정/권한 등 상위 개념)
};

// 네임스페이스(접두) - 리소스 키를 일관되게 분류
export const NAMESPACES = {
  PAGE: 'page',
  API: 'api',
};

// 네임스페이스 유틸
export const ns = {
  page: key => `${NAMESPACES.PAGE}:${key}`,
  api: key => `${NAMESPACES.API}:${key}`,
};

export const isPageResource = res =>
  String(res).startsWith(`${NAMESPACES.PAGE}:`);
export const isApiResource = res =>
  String(res).startsWith(`${NAMESPACES.API}:`);

// =========================
// 페이지 리소스 정의
// =========================
// 프로젝트 라우트 구조에 맞춰 묶음 수준으로 정의(세부 탭/세부 액션은 ACTIONS로 분리)
export const RESOURCES = {
  // ---- 공통/메인
  PAGE_HOME: ns.page('home'),
  PAGE_DASHBOARD: ns.page('dashboard'),

  // ---- 관리(Administration)
  PAGE_ADMIN_DASHBOARD: ns.page('admin_dashboard'),
  PAGE_ADMIN_USERS: ns.page('admin_users'),
  PAGE_ADMIN_WAREHOUSES: ns.page('admin_warehouses'),
  PAGE_ADMIN_VENDORS: ns.page('admin_vendors'),
  PAGE_ADMIN_SEQUENCES: ns.page('admin_sequences'),
  PAGE_ADMIN_SHIPPING_PROVIDERS: ns.page('admin_shipping_providers'),

  // ---- WMS 도메인
  PAGE_WAREHOUSE_STOCK: ns.page('warehouse_stock'),
  PAGE_PRODUCTS: ns.page('products'),
  PAGE_PURCHASE_ORDERS: ns.page('purchase_orders'),
  PAGE_GRNS: ns.page('grns'),
  PAGE_PUT_AWAY: ns.page('put_away'),
  PAGE_PICKLISTS: ns.page('picklists'),
  PAGE_SHIPMENTS: ns.page('shipments'),
  PAGE_MANIFESTS: ns.page('manifests'),
  PAGE_RETURN_MANIFESTS: ns.page('return_manifests'),
  PAGE_CHANNELS: ns.page('channels'),
  PAGE_CHANNEL_PRODUCTS: ns.page('channel_products'),
  PAGE_SEARCH: ns.page('search'),
  PAGE_SHELVES: ns.page('shelves'),
  PAGE_ORDERS: ns.page('orders'),
  PAGE_VENDORS: ns.page('vendors'),

  // =========================
  // API 리소스 정의 (도메인 단위)
  // =========================
  // NOTE:
  // - 실제 HTTP 메서드(GET/POST/PATCH/DELETE)는 ACTIONS로 대응(READ/CREATE/UPDATE/DELETE 등)
  // - /c/{tenant}/{warehouse}/ 베이스는 제외하고 "도메인 묶음" 수준으로만 식별
  API_AUTH: ns.api('auth'), // 특수(/auth) - 컨텍스트 미주입
  API_ME: ns.api('me'), // 특수(/me)   - 컨텍스트 미주입

  API_WAREHOUSES: ns.api('warehouses'),
  API_PRODUCTS: ns.api('products'),
  API_PRODUCT_NAMES: ns.api('products/name'),
  API_PRODUCT_BRAND_NAMES: ns.api('products/brands/name'),

  API_ORDERS: ns.api('orders'),
  API_PURCHASE_ORDERS: ns.api('purchase-orders'),
  API_GRNS: ns.api('grns'),
  API_PUT_AWAY: ns.api('put-away'),

  API_PICKLISTS: ns.api('picklists'),
  API_PICKLISTS_STATUS: ns.api('picklists/status'),
  API_PICKLISTS_PICKED: ns.api('picklists/picked'),
  API_PICKLISTS_SHIPMENT: ns.api('picklists/shipment'),

  API_INVENTORY: ns.api('inventory'),
  API_SHIPMENTS: ns.api('shipments'),
  API_MANIFESTS: ns.api('manifests'),
  API_RETURN_MANIFESTS: ns.api('return-manifests'),
  API_VENDORS: ns.api('vendors'),
  API_VENDOR_NAMES: ns.api('vendors/name'),

  API_SHELVES: ns.api('shelves'),
  API_CHANNELS: ns.api('channels'),
  API_CHANNEL_PRODUCTS: ns.api('channel-products'),
  API_BULK_ACTIONS: ns.api('bulk-actions'),
  API_EXPORTS: ns.api('exports'),
  API_SEARCH: ns.api('search'),

  // 관리(Management/*)
  API_MGMT_USERS: ns.api('management/users'),
  API_MGMT_WAREHOUSES: ns.api('management/warehouses'),
  API_MGMT_VENDORS: ns.api('management/vendors'),
  API_MGMT_SHELVES: ns.api('management/shelves'),
  API_MGMT_SEQUENCES: ns.api('management/prefixed-sequence'),
  API_MGMT_SHIPPING_PROVIDERS: ns.api('management/shipping-providers'),
};

// =========================
// 선택적: 리소스 그룹핑(메뉴/화면 구성에 도움)
// =========================
export const PAGE_GROUPS = {
  ADMIN: [
    RESOURCES.PAGE_ADMIN_DASHBOARD,
    RESOURCES.PAGE_ADMIN_USERS,
    RESOURCES.PAGE_ADMIN_WAREHOUSES,
    RESOURCES.PAGE_ADMIN_VENDORS,
    RESOURCES.PAGE_ADMIN_SEQUENCES,
    RESOURCES.PAGE_ADMIN_SHIPPING_PROVIDERS,
  ],
  WMS_CORE: [
    RESOURCES.PAGE_PRODUCTS,
    RESOURCES.PAGE_PURCHASE_ORDERS,
    RESOURCES.PAGE_GRNS,
    RESOURCES.PAGE_PUT_AWAY,
    RESOURCES.PAGE_PICKLISTS,
    RESOURCES.PAGE_SHIPMENTS,
    RESOURCES.PAGE_MANIFESTS,
    RESOURCES.PAGE_RETURN_MANIFESTS,
    RESOURCES.PAGE_SEARCH,
    RESOURCES.PAGE_SHELVES,
    RESOURCES.PAGE_ORDERS,
    RESOURCES.PAGE_VENDORS,
    RESOURCES.PAGE_WAREHOUSE_STOCK,
    RESOURCES.PAGE_CHANNELS,
    RESOURCES.PAGE_CHANNEL_PRODUCTS,
  ],
};

// =========================
// 헬퍼: 리소스 생성/파싱(확장용)
// =========================

/**
 * 도메인 + 세부키로 리소스 조합 (필요 시 사용)
 * e.g. combineApi('products', 'variant-prices') -> 'api:products/variant-prices'
 */
export const combineApi = (domain, subPath) =>
  ns.api(
    subPath
      ? `${String(domain).replace(/\/+$/, '')}/${String(subPath).replace(
          /^\/+/,
          '',
        )}`
      : String(domain),
  );

/**
 * 네임스페이스 제거
 * e.g. trimNamespace('api:products') -> 'products'
 */
export const trimNamespace = resource => {
  if (isPageResource(resource))
    return String(resource).slice(`${NAMESPACES.PAGE}:`.length);
  if (isApiResource(resource))
    return String(resource).slice(`${NAMESPACES.API}:`.length);
  return String(resource);
};
